import { For, Show, createSignal } from "solid-js";
import { OfferConstruct } from "~/server/types/brand";
import useScript from "~/utils/hooks/use_script";
import { ribbonConfetti, noResultsFound } from "~/assets/assets";
import {
  ButtonDarkV2,
  CloseModalButton,
} from "~/components/login-rnr/login_rnr_code_component";
import { useModal } from "~/components/modal";
import { FAQ } from "~/data/static_faqs/types";
import { PhosphorIcon } from "~/widgets/icons";
import HubbleImage from "~/widgets/hubble_image";

export function ViewOfferDetails(props: {
  offerConstruct: OfferConstruct | undefined;
  playLottie?: boolean;
}) {
  const { closeModal } = useModal()!;

  let ribbonConfettiElement: HTMLInputElement | undefined;

  const lottiePlayerExists =
    typeof window !== "undefined" &&
    window.customElements &&
    window.customElements.get("lottie-player");

  if (props.playLottie && !lottiePlayerExists) {
    useScript(
      "https://unpkg.com/@lottiefiles/lottie-player@0.3.0/dist/lottie-player.js",
      async () => {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        try {
          // @ts-ignore
          ribbonConfettiElement?.play();
        } catch (e) {
          console.error(e);
        }
      }
    );
  } else if (props.playLottie) {
    setTimeout(() => {
      try {
        // @ts-ignore
        ribbonConfettiElement?.play();
      } catch (e) {
        console.error(e);
      }
    }, 1000);
  }

  function RibbonConfettiLottie() {
    return (
      <lottie-player
        ref={ribbonConfettiElement}
        src={ribbonConfetti}
        autoplay={false}
        loop={false}
      ></lottie-player>
    );
  }

  return (
    <div class="relative flex w-full flex-col rounded-2xl bg-black px-6 darkTheme:border darkTheme:border-[#212427] md:w-[548px]">
      <div class="flex w-full flex-row items-start justify-between pt-6">
        <span class="text-nowrap text-h4 text-basePrimaryDark">
          Offer details
        </span>

        <CloseModalButton />
      </div>
      <Show
        when={
          props.offerConstruct?.termsAndConditions?.length ||
          props.offerConstruct?.faqs?.length
        }
        fallback={
          <div class="flex h-[200px] w-full flex-col items-center justify-center pb-2 pt-4">
            <HubbleImage
              src={noResultsFound}
              class="h-[80px] w-[80px]"
              alt="no offer details"
            />
            <div class="mt-3 text-center text-basePrimaryDark opacity-60">
              Offer details not available
            </div>
          </div>
        }
      >
        <Show when={props.offerConstruct?.termsAndConditions?.length}>
          <div class="pt-4"></div>
          <OfferTnc tncs={props.offerConstruct?.termsAndConditions!} />
          <div class="pt-4"></div>
        </Show>
        <Show when={props.offerConstruct?.faqs?.length}>
          <CollapsibleFaqsSection faqs={props.offerConstruct?.faqs!} />
        </Show>
      </Show>

      <div class="py-6 darkTheme:border-t darkTheme:border-[#212427]">
        <ButtonDarkV2 isLoading={false} onClick={() => closeModal()}>
          <p>Got it</p>
        </ButtonDarkV2>
      </div>
      <div class="pointer-events-none absolute left-0 right-0 top-0 flex scale-150">
        <RibbonConfettiLottie />
      </div>
    </div>
  );
}

function OfferTnc(props: { tncs: string[] }) {
  return (
    <div class="flex flex-col items-start gap-2">
      <For each={props.tncs}>
        {(item, index) => (
          <div class="text-medium text-basePrimaryDark">
            {index() + 1}. {item}
          </div>
        )}
      </For>
    </div>
  );
}
function CollapsibleFaqsSection(props: { faqs: FAQ[] }) {
  const [isFaqsExpanded, setIsFaqsExpanded] = createSignal(false);

  const toggleFaqsSection = () => {
    setIsFaqsExpanded(!isFaqsExpanded());
  };

  return (
    <div class="pt-4">
      <button
        class="flex w-full cursor-pointer items-center justify-between text-left transition-opacity hover:opacity-80"
        onClick={toggleFaqsSection}
      >
        <span class="text-nowrap text-h4 text-basePrimaryDark">
          Frequently asked questions
        </span>
        <PhosphorIcon
          name="caret-down"
          class={`transition-transform duration-200 ${isFaqsExpanded() ? "rotate-180" : ""}`}
        />
      </button>
      <div
        class="overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          "max-height": isFaqsExpanded() ? "1000px" : "0px",
          opacity: isFaqsExpanded() ? "1" : "0",
        }}
      >
        <div class="pt-4">
          <CollapsibleFaqs faqs={props.faqs} />
        </div>
      </div>
      <div class="pt-4"></div>
    </div>
  );
}

function CollapsibleFaqs(props: { faqs: FAQ[] }) {
  const [expandedIndex, setExpandedIndex] = createSignal<number | null>(null);

  const toggleExpansion = (index: number) => {
    setExpandedIndex(expandedIndex() === index ? null : index);
  };

  return (
    <div class="flex flex-col items-start gap-2 text-medium text-basePrimaryDark">
      <For each={props.faqs}>
        {(item, index) => (
          <div
            class="flex w-full cursor-pointer flex-col rounded-lg border border-gray-700 p-3 transition-colors hover:border-gray-600"
            onClick={() => toggleExpansion(index())}
          >
            <div class="flex items-center justify-between text-left font-semibold transition-opacity hover:opacity-80">
              <span>
                {index() + 1}. {item.question}
              </span>
              <PhosphorIcon
                name={expandedIndex() === index() ? "minus" : "plus"}
              />
            </div>
            <div
              class="overflow-hidden transition-all duration-300 ease-in-out"
              style={{
                "max-height": expandedIndex() === index() ? "200px" : "0px",
                opacity: expandedIndex() === index() ? "1" : "0",
              }}
            >
              <p class="ml-4 mt-3 pb-1">{item.answer}</p>
            </div>
          </div>
        )}
      </For>
    </div>
  );
}
